import { Alert } from '@mui/material';
import { TabbedForm, TabbedFormViewProps, useGetManyReference } from 'react-admin';
import { ReactElement, useEffect, useMemo, useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { LabelResource } from '../../../Entities/Label';
import { ProductLabelResource } from '../../../Entities/ProductLabel';
import ProductLabelsContainer from './Container';
import { getCharacteristicKey, toFormValue } from './utils';

interface UseHydrateProductLabelsParams {
  categoryId: string | undefined;
  existingByLabelId: Map<string, ProductLabelResource>;
  labels: LabelResource[];
  productId: string | undefined;
}

function useHydrateProductLabels({
  categoryId,
  existingByLabelId,
  labels,
  productId,
}: UseHydrateProductLabelsParams): void {
  const { getValues, setValue } = useFormContext();
  const nextCharacteristics = useMemo(
    () =>
      Object.fromEntries(
        labels.map((label) => {
          const existing = existingByLabelId.get(label.id);
          return [getCharacteristicKey(label.id), toFormValue(label, existing?.attributes.value)];
        }),
      ),
    [existingByLabelId, labels],
  );

  const nextCharacteristicsString = useMemo(
    () => JSON.stringify(nextCharacteristics),
    [nextCharacteristics],
  );

  const nextHydratedSignature = useMemo(
    () =>
      JSON.stringify({
        categoryId: categoryId ?? '',
        productId: productId ?? '',
        values: nextCharacteristics,
      }),
    [categoryId, nextCharacteristics, productId],
  );
  const lastHydratedSignatureRef = useRef('');

  useEffect(() => {
    const currentCharacteristicsString = JSON.stringify(getValues('characteristics'));

    if (
      lastHydratedSignatureRef.current === nextHydratedSignature &&
      currentCharacteristicsString === nextCharacteristicsString
    ) {
      return;
    }

    lastHydratedSignatureRef.current = nextHydratedSignature;

    if (labels.length === 0) {
      setValue('characteristics', {}, { shouldDirty: false });
      return;
    }

    // TODO: Revisit this hydration flow. The nested requestAnimationFrame calls are a
    // temporary workaround for React Hook Form / tab mount timing and are not a
    // preferred pattern to copy into new code, including AI-generated changes.
    // Replace this with a cleaner approach once we find a reliable way to hydrate these fields.
    let nestedFrame = 0;
    const frame = requestAnimationFrame(() => {
      nestedFrame = requestAnimationFrame(() => {
        for (const label of labels) {
          setValue(
            `characteristics.${getCharacteristicKey(label.id)}`,
            nextCharacteristics[getCharacteristicKey(label.id)],
            { shouldDirty: false },
          );
        }
      });
    });

    return () => {
      cancelAnimationFrame(frame);
      cancelAnimationFrame(nestedFrame);
    };
  }, [
    getValues,
    labels,
    nextCharacteristics,
    nextCharacteristicsString,
    nextHydratedSignature,
    setValue,
  ]);
}

export default function ProductLabelsTab(props: TabbedFormViewProps): ReactElement {
  const productId = useWatch({ name: 'id' }) as string | undefined;
  const categoryId = useWatch({ name: 'relationships.category.data.id' }) as string | undefined;

  const { data: labels = [], isLoading: isLabelsLoading } = useGetManyReference<LabelResource>(
    'labels',
    {
      id: categoryId ?? '',
      target: 'category.id',
      pagination: { page: 1, perPage: 250 },
      sort: { field: 'attributes.display_order', order: 'ASC' },
    },
    {
      enabled: Boolean(categoryId),
    },
  );

  const { data: productLabels = [], isLoading: isProductLabelsLoading } =
    useGetManyReference<ProductLabelResource>(
      'product_labels',
      {
        id: productId ?? '',
        target: 'product.id',
        pagination: { page: 1, perPage: 250 },
        sort: { field: 'attributes.value', order: 'ASC' },
      },
      {
        enabled: Boolean(productId),
      },
    );

  const existingByLabelId = useMemo(
    () =>
      new Map(
        productLabels
          .map((productLabel) => {
            const labelId = productLabel.id.split(':', 2)[1];
            return labelId ? [labelId, productLabel] : null;
          })
          .filter((entry): entry is [string, ProductLabelResource] => entry !== null),
      ),
    [productLabels],
  );

  useHydrateProductLabels({ categoryId, existingByLabelId, labels, productId });

  const isLoading = isLabelsLoading || isProductLabelsLoading;

  if (!categoryId) {
    return (
      <TabbedForm.Tab label="Labels" {...props}>
        <Alert severity="info">Choose a category to load labels.</Alert>
      </TabbedForm.Tab>
    );
  }

  return (
    <TabbedForm.Tab label="Labels" {...props}>
      <ProductLabelsContainer isLoading={isLoading} labels={labels} />
    </TabbedForm.Tab>
  );
}
